import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, CheckCircle, XCircle, Edit3, Save, X,
  ShoppingCart, FileText, Calendar, Filter, Package, Search,
  ChevronDown, ChevronUp, Download, Upload, Loader2, Sparkles,
  AlertTriangle, Eye, FileCheck, Truck
} from 'lucide-react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../hooks/useToast';
import { usePurchasesData, emptyLine } from '../hooks/usePurchasesData';
import { usePurchasesActions } from '../hooks/usePurchasesActions';
import type { PurchaseLine, PurchaseStatus } from '../types';

// ─── STATUS BADGE ───────────────────────────────────────────────────
const statusConfig: Record<PurchaseStatus, { label: string; color: string; bg: string; icon: typeof CheckCircle }> = {
  draft: { label: 'Brouillon', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  validated: { label: 'Validé', color: '#10b981', bg: '#ecfdf5', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: '#ef4444', bg: '#fef2f2', icon: XCircle }
};

function StatusBadge({ status }: { status: PurchaseStatus }) {
  const cfg = statusConfig[status] || statusConfig.draft;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: '6px 14px', borderRadius: '30px', fontSize: '0.8rem', fontWeight: '700',
      color: cfg.color, background: cfg.bg
    }}>
      <Icon size={14} /> {cfg.label}
    </span>
  );
}

// ─── LINE EDITOR ─────────────────────────────────────────────────────
function LineEditor({ lines, setLines }: {
  lines: (PurchaseLine | Partial<PurchaseLine>)[];
  setLines: (l: (PurchaseLine | Partial<PurchaseLine>)[]) => void;
}) {
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#475569' }}>Lignes du BL</span>
        <button
          onClick={() => setLines([...lines, emptyLine()])}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '8px', border: '1px solid #e2e8f0',
            background: 'white', cursor: 'pointer', fontSize: '0.8rem', color: '#2563eb', fontWeight: '600'
          }}
        >
          <Plus size={14} /> Ajouter une ligne
        </button>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{
          display: 'grid', gridTemplateColumns: '120px 1fr 70px 60px 100px 40px', gap: '8px',
          marginBottom: '6px', alignItems: 'center'
        }}>
          <input
            placeholder="Réf."
            value={line.reference || ''}
            onChange={e => { const l = [...lines]; l[i] = { ...l[i], reference: e.target.value }; setLines(l); }}
            style={inputStyle}
          />
          <input
            placeholder="Désignation *"
            value={line.designation || ''}
            onChange={e => { const l = [...lines]; l[i] = { ...l[i], designation: e.target.value }; setLines(l); }}
            style={inputStyle}
          />
          <input
            type="number" placeholder="Qté" min="0" step="0.01"
            value={line.quantity ?? ''}
            onChange={e => { const l = [...lines]; l[i] = { ...l[i], quantity: parseFloat(e.target.value) || 0 }; setLines(l); }}
            style={{ ...inputStyle, textAlign: 'right' as const }}
          />
          <input
            placeholder="Unité"
            value={line.unit || 'pce'}
            onChange={e => { const l = [...lines]; l[i] = { ...l[i], unit: e.target.value }; setLines(l); }}
            style={inputStyle}
          />
          <input
            type="number" placeholder="PU HT" min="0" step="0.01"
            value={line.unit_price ?? ''}
            onChange={e => { const l = [...lines]; l[i] = { ...l[i], unit_price: parseFloat(e.target.value) || 0 }; setLines(l); }}
            style={{ ...inputStyle, textAlign: 'right' as const }}
          />
          <button
            onClick={() => { const l = [...lines]; l.splice(i, 1); setLines(l.length ? l : [emptyLine()]); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
      <div style={{ textAlign: 'right', fontWeight: '700', fontSize: '0.9rem', color: '#1e293b', marginTop: '8px' }}>
        Total : {lines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0).toFixed(2)} €
      </div>
    </div>
  );
}

export default function AdminPurchases() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const data = usePurchasesData();
  const actions = usePurchasesActions(data);

  const {
    activeTab, setActiveTab,
    newSupplier, setNewSupplier,
    filterProject, setFilterProject,
    filterSupplier, setFilterSupplier,
    filterStatus, setFilterStatus,
    dateStart, setDateStart,
    dateEnd, setDateEnd,
    search, setSearch,
    showForm, setShowForm,
    formData, setFormData,
    formLines, setFormLines,
    saving,
    pdfFiles, setPdfFiles,
    extracting,
    extractionResults, setExtractionResults,
    activeResultIndex, setActiveResultIndex,
    dragOver, setDragOver,
    catalogFeedResult,
    fileInputRef,
    expandedId, setExpandedId,
    expandedLines,
    editingId, setEditingId,
    editData, setEditData,
    editLines, setEditLines,
    suppliers,
    projects,
    loading,
    filtered,
    totalHT,
  } = data;

  const {
    handleFileDrop,
    handleFileSelect,
    removePdfFile,
    matchSupplier,
    prefillForm,
    handleExtract,
    handleCreate,
    startEdit,
    saveEdit,
    handleDelete,
    handleValidate,
    addSupplier,
    deleteSupplier,
    exportExcel,
  } = actions;

  // ─── RENDU ───────────────────────────────────────────────────────────
  if (loading) {
    return <LoadingSpinner message="Chargement des achats..." />;
  }

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ─── HEADER GRADIENT ──────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '0 0 24px 24px',
        padding: '24px 30px 20px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        {/* Ligne titre + boutons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button onClick={() => navigate('/')} style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: 'white', width: '44px', height: '44px', borderRadius: '12px',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.2s'
            }}>
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'white', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShoppingCart size={28} />
                Achats & Bons de Livraison
              </h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                {filtered.length} BL · {suppliers.length} fournisseurs · Total : {totalHT.toFixed(2)} € HT
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {activeTab === 'bl' && (
              <>
                <button onClick={exportExcel} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '10px 18px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.1)', color: 'white',
                  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
                }}>
                  <Download size={16} /> Export Excel
                </button>
                <button onClick={() => setShowForm(!showForm)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '10px 18px', borderRadius: '12px', border: 'none',
                  background: 'white', color: '#1e293b',
                  fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transition: 'all 0.2s'
                }}>
                  <Plus size={16} /> Nouveau BL
                </button>
              </>
            )}
            {activeTab === 'suppliers' && (
              <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '10px 18px', borderRadius: '12px', border: 'none',
                background: 'white', color: '#1e293b',
                fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', transition: 'all 0.2s'
              }}>
                <Plus size={16} /> Ajouter un fournisseur
              </button>
            )}
          </div>
        </div>

        {/* Onglets intégrés */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setActiveTab('bl')}
            style={{
              padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px',
              background: activeTab === 'bl' ? 'white' : 'rgba(255,255,255,0.1)',
              color: activeTab === 'bl' ? '#1e293b' : 'rgba(255,255,255,0.8)',
              boxShadow: activeTab === 'bl' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <FileText size={16} /> Bons de Livraison
          </button>
          <button
            onClick={() => setActiveTab('suppliers')}
            style={{
              padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: '600', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px',
              background: activeTab === 'suppliers' ? 'white' : 'rgba(255,255,255,0.1)',
              color: activeTab === 'suppliers' ? '#1e293b' : 'rgba(255,255,255,0.8)',
              boxShadow: activeTab === 'suppliers' ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
            }}
          >
            <Truck size={16} /> Fournisseurs
            <span style={{
              background: activeTab === 'suppliers' ? '#f1f5f9' : 'rgba(255,255,255,0.15)',
              color: activeTab === 'suppliers' ? '#64748b' : 'rgba(255,255,255,0.9)',
              padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700'
            }}>{suppliers.length}</span>
          </button>
        </div>
      </div>

      {/* ─── ONGLET : BONS DE LIVRAISON ──────────────────────────────── */}
      {activeTab === 'bl' && (<>

      {/* ─── STATS SUMMARY ─────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px', marginBottom: '20px'
      }}>
        {[
          { label: 'Bons de livraison', value: filtered.length, icon: FileText, color: '#2563eb', bg: '#eff6ff' },
          { label: 'Total HT', value: `${totalHT.toFixed(2)} €`, icon: ShoppingCart, color: '#10b981', bg: '#ecfdf5' },
          { label: 'Brouillons', value: filtered.filter(p => p.status === 'draft').length, icon: Edit3, color: '#f59e0b', bg: '#fffbeb' },
          { label: 'Validés', value: filtered.filter(p => p.status === 'validated').length, icon: CheckCircle, color: '#10b981', bg: '#ecfdf5' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: 'white', padding: '18px 20px', borderRadius: '16px',
            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            display: 'flex', alignItems: 'center', gap: '14px', transition: 'all 0.2s'
          }}>
            <div style={{
              background: stat.bg, color: stat.color, padding: '10px', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <stat.icon size={20} />
            </div>
            <div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b' }}>{stat.value}</div>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontWeight: '500' }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── DROP ZONE PDF ────────────────────────────────────────────── */}
      {!showForm && (
        <div style={{ marginBottom: '16px' }}>
          {/* Zone de glisser-déposer */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.08)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)'; }}
            style={{
              background: dragOver ? '#eff6ff' : 'linear-gradient(145deg, #ffffff, #f8fafc)',
              borderRadius: '20px', border: dragOver ? '2px dashed #2563eb' : '2px dashed #cbd5e1',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              textAlign: 'center' as const,
              padding: '40px 20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: pdfFiles.length > 0 ? '12px' : '0'
            }}
          >
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: dragOver ? 'linear-gradient(135deg, #dbeafe, #bfdbfe)' : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px', transition: 'all 0.2s'
            }}>
              <Upload size={28} color={dragOver ? '#2563eb' : '#94a3b8'} />
            </div>
            <p style={{ margin: '0 0 4px', fontWeight: '600', color: '#475569', fontSize: '0.95rem' }}>
              Glissez vos bons de livraison PDF ici
            </p>
            <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.8rem' }}>
              ou cliquez pour sélectionner • Rexel, Sonepar, Sermes, etc.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File d'attente des PDFs */}
          {pdfFiles.length > 0 && (
            <div style={{ ...cardStyle, padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#475569', fontSize: '0.85rem' }}>
                  <FileText size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  {pdfFiles.length} fichier{pdfFiles.length > 1 ? 's' : ''} sélectionné{pdfFiles.length > 1 ? 's' : ''}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setPdfFiles([]); setExtractionResults([]); }}
                    style={{ ...secondaryBtnStyle, padding: '6px 12px', fontSize: '0.8rem' }}
                  >
                    <Trash2 size={14} /> Tout retirer
                  </button>
                  <button
                    onClick={handleExtract}
                    disabled={extracting}
                    style={{ ...primaryBtnStyle, padding: '6px 14px', fontSize: '0.8rem', background: '#7c3aed', opacity: extracting ? 0.6 : 1 }}
                  >
                    {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {extracting ? 'Analyse OCR intelligente en cours...' : 'Analyser les BL'}
                  </button>
                </div>
              </div>

              {/* Liste des fichiers */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {pdfFiles.map((f, i) => {
                  const result = extractionResults.find(r => r.file === f);
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 12px', background: '#f8fafc', borderRadius: '10px',
                      border: result?.error ? '1px solid #fecaca' : result?.result ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                    }}>
                      {result?.result ? (
                        <FileCheck size={16} color="#10b981" />
                      ) : result?.error ? (
                        <AlertTriangle size={16} color="#ef4444" />
                      ) : (
                        <FileText size={16} color="#94a3b8" />
                      )}
                      <span style={{ flex: 1, fontSize: '0.85rem', color: '#334155', fontWeight: '500' }}>
                        {f.name}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                        {(f.size / 1024).toFixed(0)} Ko
                      </span>
                      {result?.result && (
                        <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '600' }}>
                          {result.result.lines.length} ligne{result.result.lines.length > 1 ? 's' : ''}
                        </span>
                      )}
                      {result?.error && (
                        <span style={{ fontSize: '0.75rem', color: '#ef4444', fontWeight: '600' }}>
                          Erreur
                        </span>
                      )}
                      {!extracting && !result && (
                        <button
                          onClick={() => removePdfFile(i)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px' }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Résultats d'extraction — onglets + résumé */}
          {extractionResults.length > 0 && (
            <div style={{ ...cardStyle, padding: '16px', marginTop: '12px', borderLeft: '4px solid #7c3aed' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Sparkles size={16} color="#7c3aed" />
                <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.9rem' }}>
                  Résultats de l'extraction
                </span>
              </div>

              {/* Onglets si plusieurs PDFs */}
              {extractionResults.length > 1 && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {extractionResults.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setActiveResultIndex(i);
                        if (r.result) void prefillForm(r.result);
                      }}
                      style={{
                        padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem',
                        border: i === activeResultIndex ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                        background: i === activeResultIndex ? '#f5f3ff' : 'white',
                        color: r.error ? '#ef4444' : i === activeResultIndex ? '#7c3aed' : '#64748b',
                        fontWeight: i === activeResultIndex ? '700' : '500',
                        cursor: 'pointer'
                      }}
                    >
                      {r.error ? '✗' : '✓'} {r.file.name.replace('.pdf', '').substring(0, 20)}
                    </button>
                  ))}
                </div>
              )}

              {/* Résumé du résultat actif */}
              {(() => {
                const current = extractionResults[activeResultIndex];
                if (!current) return null;
                if (current.error) return (
                  <div style={{ color: '#ef4444', fontSize: '0.85rem' }}>
                    <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Impossible d'extraire les données : {current.error}
                  </div>
                );
                const bl = current.result!;
                const matchedSupplier = matchSupplier(bl.supplier_name);
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', fontSize: '0.85rem' }}>
                    <div style={summaryItemStyle}>
                      <span style={summaryLabelStyle}>Fournisseur</span>
                      <span style={{ color: matchedSupplier ? '#10b981' : '#f59e0b', fontWeight: '600' }}>
                        {bl.supplier_name || '—'} {matchedSupplier ? '✓' : '(non trouvé en BDD)'}
                      </span>
                    </div>
                    <div style={summaryItemStyle}>
                      <span style={summaryLabelStyle}>N° BL</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>{bl.bl_number || '—'}</span>
                    </div>
                    <div style={summaryItemStyle}>
                      <span style={summaryLabelStyle}>Date</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {bl.bl_date ? new Date(bl.bl_date).toLocaleDateString('fr-FR') : '—'}
                      </span>
                    </div>
                    <div style={summaryItemStyle}>
                      <span style={summaryLabelStyle}>Lignes</span>
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>{bl.lines.length}</span>
                    </div>
                    <div style={summaryItemStyle}>
                      <span style={summaryLabelStyle}>Total HT</span>
                      <span style={{ fontWeight: '700', color: '#2563eb', fontSize: '1rem' }}>
                        {bl.total_ht?.toFixed(2) ?? '—'} €
                      </span>
                    </div>
                  </div>
                );
              })()}

              {/* Bouton pour utiliser le résultat */}
              {extractionResults[activeResultIndex]?.result && !showForm && (
                <div style={{ marginTop: '12px', textAlign: 'right' }}>
                  <button
                    onClick={() => void prefillForm(extractionResults[activeResultIndex].result!)}
                    style={{ ...primaryBtnStyle, background: '#7c3aed' }}
                  >
                    <Eye size={16} /> Vérifier et enregistrer ce BL
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* FORMULAIRE CRÉATION */}
      {showForm && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px' }}>
              {extractionResults.length > 0 ? (
                <>
                  <div style={{ background: '#f3e8ff', padding: '8px', borderRadius: '10px', color: '#7c3aed', display: 'flex' }}>
                    <Sparkles size={18} />
                  </div>
                  Vérification du BL extrait
                </>
              ) : (
                <>
                  <div style={{ background: '#eff6ff', padding: '8px', borderRadius: '10px', color: '#2563eb', display: 'flex' }}>
                    <FileText size={18} />
                  </div>
                  Nouveau Bon de Livraison
                </>
              )}
            </h3>
            {extractionResults.length > 1 && (
              <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: '600' }}>
                BL {activeResultIndex + 1} / {extractionResults.filter(r => r.result).length}
              </span>
            )}
          </div>

          {/* Alertes champs manquants après extraction OCR */}
          {extractionResults.length > 0 && !formData.project_id && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '10px', marginBottom: '12px',
              background: '#fefce8', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.85rem'
            }}>
              <AlertTriangle size={16} />
              <span>L'affaire n'a pas été détectée dans le PDF — sélectionnez-la manuellement.</span>
            </div>
          )}
          {extractionResults.length > 0 && !formData.supplier_id && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '10px', marginBottom: '12px',
              background: '#fefce8', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.85rem'
            }}>
              <AlertTriangle size={16} />
              <span>Le fournisseur n'a pas été identifié — sélectionnez-le manuellement.</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
            <div>
              <label style={labelStyle}>Affaire *</label>
              <select
                value={formData.project_id}
                onChange={e => setFormData({ ...formData, project_id: e.target.value })}
                style={{
                  ...inputStyle,
                  ...(extractionResults.length > 0 && !formData.project_id ? { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245,158,11,0.2)' } : {})
                }}
              >
                <option value="">— Sélectionner —</option>
                {projects.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Fournisseur *</label>
              <select value={formData.supplier_id} onChange={e => setFormData({ ...formData, supplier_id: e.target.value })} style={{
                ...inputStyle,
                ...(extractionResults.length > 0 && !formData.supplier_id ? { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245,158,11,0.2)' } : {})
              }}>
                <option value="">— Sélectionner —</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>N° BL</label>
              <input value={formData.bl_number} onChange={e => setFormData({ ...formData, bl_number: e.target.value })} placeholder="Optionnel" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Date BL</label>
              <input type="date" value={formData.bl_date} onChange={e => setFormData({ ...formData, bl_date: e.target.value })} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Optionnel" rows={2} style={{ ...inputStyle, resize: 'vertical' as const }} />
          </div>

          <LineEditor lines={formLines} setLines={setFormLines} />

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button onClick={() => { setShowForm(false); setFormLines([emptyLine()]); }} style={secondaryBtnStyle}>
              <X size={16} /> Annuler
            </button>
            <button
              onClick={() => {
                if (!formData.project_id) { toast.warning('Veuillez sélectionner une affaire.'); return; }
                if (!formData.supplier_id) { toast.warning('Veuillez sélectionner un fournisseur.'); return; }
                if (formLines.every(l => !l.designation)) { toast.warning('Ajoutez au moins une ligne avec une désignation.'); return; }
                handleCreate();
              }}
              disabled={saving}
              style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}
            >
              <Save size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>

          {/* Résumé alimentation catalogue */}
          {catalogFeedResult && (
            <div style={{
              marginTop: '16px', padding: '14px 18px', borderRadius: '12px',
              background: catalogFeedResult.errors.length > 0 ? '#fffbeb' : '#ecfdf5',
              border: `1px solid ${catalogFeedResult.errors.length > 0 ? '#fde68a' : '#86efac'}`,
              display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.85rem'
            }}>
              <CheckCircle size={18} color={catalogFeedResult.errors.length > 0 ? '#f59e0b' : '#10b981'} />
              <div>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Catalogue alimenté : </span>
                {catalogFeedResult.materialsCreated > 0 && (
                  <span style={{ color: '#10b981', fontWeight: '600' }}>
                    {catalogFeedResult.materialsCreated} article{catalogFeedResult.materialsCreated > 1 ? 's' : ''} ajouté{catalogFeedResult.materialsCreated > 1 ? 's' : ''}
                  </span>
                )}
                {catalogFeedResult.materialsCreated > 0 && catalogFeedResult.pricesRecorded > 0 && ', '}
                {catalogFeedResult.pricesRecorded > 0 && (
                  <span style={{ color: '#2563eb', fontWeight: '600' }}>
                    {catalogFeedResult.pricesRecorded} prix enregistré{catalogFeedResult.pricesRecorded > 1 ? 's' : ''}
                  </span>
                )}
                {catalogFeedResult.errors.length > 0 && (
                  <span style={{ color: '#f59e0b', marginLeft: '8px' }}>
                    ({catalogFeedResult.errors.length} erreur{catalogFeedResult.errors.length > 1 ? 's' : ''})
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* FILTRES */}
      <div style={{
        ...cardStyle, marginBottom: '20px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ background: '#f1f5f9', padding: '8px', borderRadius: '10px', color: '#475569', display: 'flex' }}>
            <Filter size={16} />
          </div>
          <span style={{ fontWeight: '700', color: '#0f172a', fontSize: '0.95rem' }}>Filtres</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}><Calendar size={12} /> Du</label>
            <input type="date" value={dateStart} onChange={e => setDateStart(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}><Calendar size={12} /> Au</label>
            <input type="date" value={dateEnd} onChange={e => setDateEnd(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}><Package size={12} /> Affaire</label>
            <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={inputStyle}>
              <option value="all">Toutes</option>
              {projects.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}><ShoppingCart size={12} /> Fournisseur</label>
            <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)} style={inputStyle}>
              <option value="all">Tous</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Statut</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as PurchaseStatus | 'all')} style={inputStyle}>
              <option value="all">Tous</option>
              <option value="draft">Brouillon</option>
              <option value="validated">Validé</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}><Search size={12} /> Recherche</label>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="BL, affaire, fournisseur..." style={inputStyle} />
          </div>
        </div>
      </div>

      {/* LISTE DES BL */}
      {filtered.length === 0 ? (
        <EmptyState
          message="Aucun bon de livraison sur cette période"
          icon={<ShoppingCart size={36} />}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(p => {
            const isExpanded = expandedId === p.id;
            const isEditing = editingId === p.id;

            return (
              <div key={p.id}
                onMouseOver={e => { if (!isExpanded) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 20px 35px -10px rgba(0,0,0,0.1), 0 8px 15px -5px rgba(0,0,0,0.04)'; } }}
                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)'; }}
                style={{
                  ...cardStyle,
                  borderRadius: '20px',
                  borderLeft: `4px solid ${statusConfig[p.status]?.color || '#64748b'}`,
                  padding: 0,
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.03)',
                  transition: 'all 0.2s ease'
                }}>
                {/* En-tête du BL */}
                <div
                  onClick={() => { if (!isEditing) setExpandedId(isExpanded ? null : p.id); }}
                  style={{
                    padding: '16px 20px', cursor: isEditing ? 'default' : 'pointer',
                    display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '12px', alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>
                        {p.project?.name}
                      </span>
                      {p.bl_number && (
                        <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '6px', fontSize: '0.8rem', color: '#475569', fontWeight: '600' }}>
                          BL {p.bl_number}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '0.8rem', color: '#94a3b8' }}>
                      <span>{new Date(p.bl_date).toLocaleDateString('fr-FR')}</span>
                      <span>{p.supplier?.name}</span>
                      {p.employee?.full_name && <span>par {p.employee.full_name}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1e293b' }}>
                      {p.total_ht.toFixed(2)} €
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  <div>
                    {isExpanded ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
                  </div>
                </div>

                {/* Détail (lignes) */}
                {isExpanded && !isEditing && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px' }}>
                    {expandedLines.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>Aucune ligne</p>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={thStyle}>Réf.</th>
                            <th style={thStyle}>Désignation</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Qté</th>
                            <th style={thStyle}>Unité</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>PU HT</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Total HT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expandedLines.map(line => (
                            <tr key={line.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                              <td style={tdStyle}>{line.reference || '—'}</td>
                              <td style={tdStyle}>{line.designation}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{line.quantity}</td>
                              <td style={tdStyle}>{line.unit}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{line.unit_price.toFixed(2)} €</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{line.total_price.toFixed(2)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {p.notes && <p style={{ marginTop: '12px', fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>Note : {p.notes}</p>}

                    {/* Lien vers le PDF source */}
                    {p.pdf_url && (
                      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <a
                          href={p.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', borderRadius: '10px',
                            background: '#eff6ff', color: '#2563eb', border: '1px solid #dbeafe',
                            textDecoration: 'none', fontSize: '0.8rem', fontWeight: '600',
                            transition: 'all 0.2s'
                          }}
                          onMouseOver={e => { e.currentTarget.style.background = '#dbeafe'; }}
                          onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; }}
                        >
                          <Eye size={14} /> Voir le PDF source
                        </a>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
                      {p.status === 'draft' && (
                        <>
                          <button onClick={() => startEdit(p)} style={secondaryBtnStyle}><Edit3 size={14} /> Modifier</button>
                          <button onClick={() => handleValidate(p.id)} style={{ ...primaryBtnStyle, background: '#10b981' }}><CheckCircle size={14} /> Valider</button>
                          <button onClick={() => handleDelete(p.id)} style={{ ...secondaryBtnStyle, color: '#ef4444', borderColor: '#fecaca' }}><Trash2 size={14} /> Supprimer</button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Mode édition */}
                {isExpanded && isEditing && (
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <label style={labelStyle}>N° BL</label>
                        <input value={editData.bl_number} onChange={e => setEditData({ ...editData, bl_number: e.target.value })} style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Statut</label>
                        <select value={editData.status} onChange={e => setEditData({ ...editData, status: e.target.value as PurchaseStatus })} style={inputStyle}>
                          <option value="draft">Brouillon</option>
                          <option value="validated">Validé</option>
                          <option value="cancelled">Annulé</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={labelStyle}>Notes</label>
                      <textarea value={editData.notes} onChange={e => setEditData({ ...editData, notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' as const }} />
                    </div>
                    <LineEditor lines={editLines} setLines={setEditLines} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                      <button onClick={() => { setEditingId(null); }} style={secondaryBtnStyle}><X size={14} /> Annuler</button>
                      <button onClick={saveEdit} disabled={saving} style={primaryBtnStyle}><Save size={14} /> {saving ? 'Enregistrement...' : 'Sauvegarder'}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </>)}

      {/* ─── ONGLET : FOURNISSEURS ───────────────────────────────────── */}
      {activeTab === 'suppliers' && (
        <div>
          {/* Formulaire ajout fournisseur */}
          <div style={{ ...cardStyle, marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: '#eff6ff', padding: '10px', borderRadius: '12px', color: '#2563eb'
              }}>
                <Plus size={18} />
              </div>
              Nouveau Fournisseur
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
              <div>
                <label style={labelStyle}>Nom entreprise *</label>
                <input
                  value={newSupplier.name}
                  onChange={e => setNewSupplier({ ...newSupplier, name: e.target.value })}
                  placeholder="Ex: Rexel, Sonepar..."
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Contact</label>
                <input
                  value={newSupplier.contact_name}
                  onChange={e => setNewSupplier({ ...newSupplier, contact_name: e.target.value })}
                  placeholder="Nom du commercial"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Téléphone</label>
                <input
                  value={newSupplier.phone}
                  onChange={e => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                  placeholder="02 43 ..."
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Catégorie</label>
                <input
                  value={newSupplier.category}
                  onChange={e => setNewSupplier({ ...newSupplier, category: e.target.value })}
                  placeholder="Ex: Électricité, Plomberie..."
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button
                onClick={addSupplier}
                disabled={!newSupplier.name.trim()}
                style={{ ...primaryBtnStyle, opacity: !newSupplier.name.trim() ? 0.5 : 1 }}
              >
                <Plus size={16} /> Ajouter
              </button>
            </div>
          </div>

          {/* Liste des fournisseurs */}
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden', borderRadius: '20px' }}>
            {/* En-tête tableau */}
            <div style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              padding: '18px 20px', borderBottom: '1px solid #e2e8f0',
              display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 60px', gap: '12px', alignItems: 'center'
            }}>
              <span style={tableLabelStyle}>Nom</span>
              <span style={tableLabelStyle}>Contact</span>
              <span style={tableLabelStyle}>Téléphone</span>
              <span style={tableLabelStyle}>Catégorie</span>
              <span style={{ ...tableLabelStyle, textAlign: 'right' }}>Action</span>
            </div>

            {suppliers.length === 0 ? (
              <EmptyState
                message="Aucun fournisseur"
                icon={<Truck size={36} />}
              />
            ) : (
              suppliers.map((s, index) => (
                <div
                  key={s.id}
                  onMouseOver={e => { e.currentTarget.style.background = '#fafbfc'; }}
                  onMouseOut={e => { e.currentTarget.style.background = 'white'; }}
                  style={{
                    padding: '18px 20px',
                    display: 'grid', gridTemplateColumns: '2fr 1.5fr 1.5fr 1fr 60px', gap: '12px', alignItems: 'center',
                    borderBottom: index < suppliers.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: 'white', transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600', color: '#1e293b' }}>
                    <div style={{
                      width: '40px', height: '40px', background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', borderRadius: '12px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0
                    }}>
                      <Truck size={18} />
                    </div>
                    {s.name}
                  </div>
                  <div style={{ color: '#475569', fontSize: '0.9rem' }}>{s.contact_name || '—'}</div>
                  <div style={{ color: '#475569', fontSize: '0.9rem', fontFamily: 'monospace' }}>{s.phone || '—'}</div>
                  <div>
                    {s.category ? (
                      <span style={{
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        color: '#2563eb', padding: '6px 14px',
                        borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #bfdbfe'
                      }}>{s.category}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => deleteSupplier(s.id)}
                      onMouseOver={e => { e.currentTarget.style.background = '#fff1f2'; e.currentTarget.style.borderColor = '#f87171'; }}
                      onMouseOut={e => { e.currentTarget.style.background = 'linear-gradient(145deg, #ffffff, #fff1f2)'; e.currentTarget.style.borderColor = '#fecaca'; }}
                      style={{
                        background: 'linear-gradient(145deg, #ffffff, #fff1f2)',
                        border: '1px solid #fecaca', borderRadius: '10px',
                        padding: '10px', cursor: 'pointer', color: '#ef4444',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.2s ease'
                      }}
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────
const cardStyle: React.CSSProperties = {
  background: 'white', borderRadius: '20px',
  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
  border: '1px solid #e2e8f0', padding: '24px', marginBottom: '16px'
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #cbd5e1',
  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
  background: 'white', transition: 'all 0.2s'
};
const labelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '6px',
  fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px'
};
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '12px 22px', borderRadius: '12px', border: 'none',
  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(37,99,235,0.2)', transition: 'all 0.2s ease'
};
const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '12px 22px', borderRadius: '12px', border: '1px solid #cbd5e1',
  background: '#f1f5f9', color: '#475569',
  fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s ease'
};
const thStyle: React.CSSProperties = { padding: '10px 8px', fontWeight: '700', fontSize: '0.8rem' };
const tdStyle: React.CSSProperties = { padding: '10px 8px' };
const summaryItemStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '2px' };
const summaryLabelStyle: React.CSSProperties = { fontSize: '0.7rem', textTransform: 'uppercase', color: '#94a3b8', fontWeight: '600', letterSpacing: '0.05em' };
const tableLabelStyle: React.CSSProperties = { fontSize: '0.8rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
